// Lightweight per-route SEO helper. Sets title, description, canonical,
// og:title/og:description/og:url, and an optional JSON-LD <script>.
// Restores previous values on unmount.

const SITE_URL = "https://getstackly.tech";

type SeoOptions = {
  title: string;
  description: string;
  path: string; // e.g. "/about" — leading slash
  jsonLd?: object | object[];
  jsonLdId?: string;
  ogType?: string; // defaults to existing value (sitewide "website")
  ogImage?: string; // absolute URL
  twitterTitle?: string;
  twitterDescription?: string;
};

function upsertMeta(selector: string, attr: "name" | "property", key: string, content: string) {
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute("content") ?? "";
  el.setAttribute("content", content);
  return () => {
    if (created) el!.remove();
    else el!.setAttribute("content", prev);
  };
}

function upsertCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  let created = false;
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.href;
  el.href = href;
  return () => {
    if (created) el!.remove();
    else el!.href = prev;
  };
}

export function applySeo({
  title,
  description,
  path,
  jsonLd,
  jsonLdId,
  ogType,
  ogImage,
  twitterTitle,
  twitterDescription,
}: SeoOptions) {
  const url = `${SITE_URL}${path}`;
  const prevTitle = document.title;
  document.title = title;

  const restorers: Array<() => void> = [
    () => { document.title = prevTitle; },
    upsertMeta('meta[name="description"]', "name", "description", description),
    upsertMeta('meta[property="og:title"]', "property", "og:title", title),
    upsertMeta('meta[property="og:description"]', "property", "og:description", description),
    upsertMeta('meta[property="og:url"]', "property", "og:url", url),
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", twitterTitle ?? title),
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", twitterDescription ?? description),
    upsertCanonical(url),
  ];

  if (ogType) {
    restorers.push(upsertMeta('meta[property="og:type"]', "property", "og:type", ogType));
  }
  if (ogImage) {
    restorers.push(upsertMeta('meta[property="og:image"]', "property", "og:image", ogImage));
    restorers.push(upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImage));
  }


  if (jsonLd) {
    const id = jsonLdId ?? "route-jsonld";
    document.getElementById(id)?.remove();
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = id;
    s.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(s);
    restorers.push(() => document.getElementById(id)?.remove());
  }

  return () => restorers.forEach((fn) => fn());
}
